import { hasConsent } from "./consent";

const COMPANY_ID = /^[A-Za-z0-9]+$/;

let previousUrl = null;
let lastIdentifiedEmail = "";
let anonymousRequested = false;
const sessionFlags = new Set();

export function klaviyoCompanyId(raw) {
  const key = typeof raw === "string" ? raw.trim() : "";
  return COMPANY_ID.test(key) ? key : "";
}

export function klaviyoScriptSrc(raw) {
  const key = klaviyoCompanyId(raw);
  return key ? `https://static.klaviyo.com/onsite/js/${key}/klaviyo.js` : "";
}

export function pageUrl(pathname, searchStr) {
  const path = pathname || "/";
  const search = !searchStr ? "" : searchStr.startsWith("?") ? searchStr : `?${searchStr}`;
  if (typeof window === "undefined") return `${path}${search}`;
  return `${window.location.origin}${path}${search}`;
}

/** First URL and later URLs are each sent once. The same URL is not sent again. */
export function shouldTrackClientPageView(previousUrl, nextUrl) {
  if (!nextUrl) return false;
  return previousUrl !== nextUrl;
}

function companyId() {
  return klaviyoCompanyId(process.env.REACT_APP_KLAVIYO_PUBLIC_KEY);
}

function push(command) {
  if (!hasConsent() || !companyId() || typeof window === "undefined") return false;
  window._learnq = window._learnq || [];
  if (!anonymousRequested) {
    anonymousRequested = true;
    window._learnq.push(["enableAnonymousTracking"]);
  }
  window._learnq.push(command);
  return true;
}

export function ensureKlaviyoScript() {
  const src = klaviyoScriptSrc(process.env.REACT_APP_KLAVIYO_PUBLIC_KEY);
  if (!hasConsent() || !src || typeof document === "undefined") return;
  if (document.querySelector('script[src*="static.klaviyo.com/onsite/js/"]')) return;
  const script = document.createElement("script");
  script.async = true;
  script.type = "text/javascript";
  script.src = src;
  document.head.appendChild(script);
}

export function identifyKlaviyo(email) {
  const normalized = typeof email === "string" ? email.trim().toLowerCase() : "";
  if (!normalized || normalized === lastIdentifiedEmail) return;
  if (!push(["identify", { $email: normalized }])) return;
  lastIdentifiedEmail = normalized;
}

export function trackClientPageView(url, page, accountType) {
  if (!hasConsent() || !companyId() || !url) return;
  if (!shouldTrackClientPageView(previousUrl, url)) return;
  previousUrl = url;
  const properties = { url, page };
  if (accountType) properties.account_type = accountType;
  push(["track", "Viewed Page", properties]);
}

function oncePerSession(key) {
  if (sessionFlags.has(key)) return false;
  try {
    if (sessionStorage.getItem(key)) {
      sessionFlags.add(key);
      return false;
    }
    sessionStorage.setItem(key, "1");
  } catch {
    // storage blocked: the in-memory flag still stops a second push this load
  }
  sessionFlags.add(key);
  return true;
}

export function trackRegistration(status, accountType, email) {
  if (!hasConsent() || !companyId()) return;
  if (status !== "started" && status !== "completed") return;
  if (!oncePerSession(`klaviyo:registration-${status}:${accountType}`)) return;
  if (status === "completed") identifyKlaviyo(email);
  push([
    "track",
    status === "started" ? "Registration Started" : "Registration Completed",
    {
      url: window.location.href,
      account_type: accountType,
      registration_status: status,
    },
  ]);
}

export function readStoredUserEmail() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    return typeof user?.email === "string" ? user.email : "";
  } catch {
    return "";
  }
}
